import { logError } from '@utils/usefulFuncs';

/**
 * Returns a deep plain structure safe for JSON.stringify: no cycles, no functions/symbols,
 * no DOM nodes. Duplicate object references (DAG) are cloned as separate subtrees, matching
 * JSON.stringify duplication behavior. Circular references become `null` and are logged.
 */
export function sanitizeJsonRequestBody(payload) {
  const visit = (value, path, stack) => {
    if (value === undefined) {
      return undefined;
    }
    if (value === null) {
      return null;
    }
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      return value;
    }
    if (t === 'bigint') {
      return String(value);
    }
    if (t === 'function' || t === 'symbol') {
      logError('Removed non-JSON value from API request body', { path, type: t });
      return undefined;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) {
      logError('Removed DOM node from API request body', { path });
      return undefined;
    }
    if (typeof File !== 'undefined' && value instanceof File) {
      logError('Removed File from API request body', { path });
      return undefined;
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      logError('Removed Blob from API request body', { path });
      return undefined;
    }

    if (typeof value === 'object') {
      if (stack.has(value)) {
        logError('Circular reference in API request body', { path });
        return null;
      }
      stack.add(value);
      try {
        if (Array.isArray(value)) {
          return value.map((item, i) => {
            const next = visit(item, `${path}[${i}]`, stack);
            return next === undefined ? null : next;
          });
        }
        const out = {};
        for (const key of Object.keys(value)) {
          const next = visit(value[key], path ? `${path}.${key}` : key, stack);
          if (next !== undefined) {
            out[key] = next;
          }
        }
        return out;
      } finally {
        stack.delete(value);
      }
    }

    logError('Unsupported value in API request body', { path, type: t });
    return null;
  };

  return visit(payload, '', new Set());
}
